import { useMemo, useState } from 'react';

import { BigQuerySyncDiffBeforeAfterPair } from '@/components/molecules/bigQuerySyncPreview/BigQuerySyncDiffBeforeAfterPair';
import { BigQuerySyncDiffJsonBlock } from '@/components/molecules/bigQuerySyncPreview/BigQuerySyncDiffJsonBlock';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { BigQuerySyncPreviewDiffLineGql } from '@/graphql/types/bigQuerySyncPreview';
import { filterBigQueryDiffLinesByQuery } from '@/services/bigQuerySyncPreview/filterBigQueryDiffLinesByQuery';

type BigQuerySyncDiffTableProps = {
  lines: BigQuerySyncPreviewDiffLineGql[];
};

function opBadgeVariant(op: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  const key = op.toUpperCase();
  if (key.includes('DEL') || key === 'REMOVE') return 'destructive';
  if (key.includes('ADD') || key.includes('INS')) return 'secondary';
  return 'outline';
}

export function BigQuerySyncDiffTable({ lines }: BigQuerySyncDiffTableProps) {
  const [clientQuery, setClientQuery] = useState('');

  const filtered = useMemo(() => filterBigQueryDiffLinesByQuery(lines, clientQuery), [
    lines,
    clientQuery,
  ]);

  if (lines.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid max-w-lg gap-2">
        <Label htmlFor="bq-diff-client-search">Buscar nas linhas já carregadas</Label>
        <Input
          id="bq-diff-client-search"
          placeholder="Entidade, operação ou trecho do JSON…"
          value={clientQuery}
          onChange={(e) => setClientQuery(e.target.value)}
          autoComplete="off"
        />
        {clientQuery.trim() && filtered.length !== lines.length ? (
          <p className="text-xs text-muted-foreground">
            Mostrando {filtered.length} de {lines.length} linha(s).
          </p>
        ) : null}
      </div>

      {filtered.length === 0 ? (
        <p className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-6 text-center text-sm text-muted-foreground">
          Nenhuma linha corresponde à busca. Limpe o campo ou ajuste o texto.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border shadow-xs">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-card shadow-sm">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10 bg-card text-center text-xs font-semibold">#</TableHead>
                <TableHead className="min-w-[100px] bg-card">Entidade</TableHead>
                <TableHead className="w-[100px] bg-card">Op</TableHead>
                <TableHead className="min-w-[160px] bg-card">Chave natural</TableHead>
                <TableHead className="min-w-[min(90vw,48rem)] bg-card">
                  Antes · Depois (comparado)
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((line, index) => (
                <TableRow
                  key={`${line.entity}-${line.op}-${index}`}
                  className="align-top transition-colors hover:bg-muted/30"
                >
                  <TableCell className="bg-muted/10 text-center font-mono text-xs text-muted-foreground">
                    {index + 1}
                  </TableCell>
                  <TableCell className="max-w-[140px] align-top text-xs font-medium break-words">
                    {line.entity}
                  </TableCell>
                  <TableCell className="align-top">
                    <Badge variant={opBadgeVariant(line.op)} className="font-mono text-[0.7rem]">
                      {line.op}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[220px] align-top p-2">
                    <BigQuerySyncDiffJsonBlock value={line.natural_key_json} />
                  </TableCell>
                  <TableCell className="min-w-[min(88vw,52rem)] align-top p-2">
                    <BigQuerySyncDiffBeforeAfterPair
                      beforeJson={line.before_json}
                      afterJson={line.after_json}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
